import { observer } from "mobx-react";
import { useState } from "react";
import axios from "axios";
import { PlusOutlined } from "@ant-design/icons";
import { Modal, Form, Input, message, Upload } from "antd";
import type { RcFile, UploadFile } from "antd/es/upload/interface";
import { AppServiceInstance } from "../../services/app";
import {
  createSupportRequest,
  presignSupportAttachmentUpload,
} from "../../axios/routes/support";
import { buildSupportRequestContext } from "../../utils/supportRequestContext";
import { FontFamilyTypeEnum } from "../styled";

const { TextArea } = Input;

const MAX_SUPPORT_FILES = 3;
const MAX_IMAGE_MB = 8;

const normFile = (e: { fileList?: UploadFile[] }) => {
  if (Array.isArray(e)) return e;
  return e?.fileList;
};

async function uploadSupportFeedbackAttachments(fileList: UploadFile[]): Promise<string[]> {
  const files = fileList
    .map((entry) => entry.originFileObj as File | undefined)
    .filter((f): f is File => Boolean(f));

  const urls: string[] = [];
  await files.reduce<Promise<void>>(
    (prev, file) => prev.then(async () => {
      const presign = await presignSupportAttachmentUpload(file.type);
      await axios.put(presign.uploadURL, file, {
        headers: { "Content-Type": file.type },
      });
      urls.push(presign.imageShortUrl);
    }),
    Promise.resolve(),
  );
  return urls;
}

const isFeatureRequest = (source?: string) => source === "feature_request";
const isRequestTeam = (source?: string) => source === "request_team";

export const SupportFeedbackModal = observer((): JSX.Element | null => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const source = AppServiceInstance.supportFeedbackSource;
  const isFeature = isFeatureRequest(source);
  const isTeamRequest = isRequestTeam(source);
  const showAttachments = !isTeamRequest;

  const beforeUploadSupportImage = (file: RcFile) => {
    const okType = ["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type);
    if (!okType) {
      message.error("Please use JPG, PNG, WebP, or GIF.");
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      message.error(`Each image must be ${MAX_IMAGE_MB} MB or smaller.`);
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const ctx = buildSupportRequestContext();

      const attachmentUrls = showAttachments
        ? await uploadSupportFeedbackAttachments((values.attachments || []) as UploadFile[])
        : [];

      await createSupportRequest({
        email: values.email,
        message: values.message,
        source,
        pageHost: ctx.pageHost,
        gameContext: ctx.gameContext,
        seatContext: ctx.seatContext,
        ...(attachmentUrls.length ? { attachmentUrls } : {}),
      });
      message.success(
        isTeamRequest
          ? "Thanks! We'll review your team request."
          : isFeature
            ? "Thank you for your suggestion! We appreciate your feedback."
            : "Thank you! We'll get back to you soon.",
      );
      form.resetFields();
      AppServiceInstance.closeSupportFeedback();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    AppServiceInstance.closeSupportFeedback();
  };

  return (
    <Modal
      title={isTeamRequest ? "Request a Team" : isFeature ? "Request a Feature" : "Feedback or Help"}
      visible={AppServiceInstance.supportFeedbackOpen}
      onOk={handleSubmit}
      onCancel={handleCancel}
      okText="Submit"
      confirmLoading={submitting}
      destroyOnClose
      maskClosable={false}
      width={480}
      style={{ fontFamily: FontFamilyTypeEnum.MONTSERRAT }}
    >
      {(isFeature || isTeamRequest) && (
        <p style={{
          fontSize: 14,
          color: "#374151",
          lineHeight: 1.6,
          margin: "16px 0 8px",
          fontFamily: FontFamilyTypeEnum.MONTSERRAT,
        }}
        >
          {isTeamRequest
            ? "We value your feedback! Let us know which team you would like Momento to go to next!"
            : "We value your feedback! Help us improve the experience with new ideas and features."}
        </p>
      )}
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: (isFeature || isTeamRequest) ? 8 : 16 }}
        initialValues={{ attachments: [] }}
      >
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: "Please enter your email" },
            { type: "email", message: "Please enter a valid email" },
          ]}
        >
          <Input placeholder="your@email.com" type="email" />
        </Form.Item>
        <Form.Item
          name="message"
          label={isFeature || isTeamRequest ? "Your Idea" : "Question or Issue"}
          rules={[{
            required: true,
            message: isFeature || isTeamRequest
              ? "Please describe your feature idea"
              : "Please enter your question or issue",
          }]}
        >
          <TextArea
            placeholder={
              isFeature
                ? "Tell us about a feature you'd love to see..."
                : isTeamRequest
                  ? "Tell us which team you'd like to see on Momento next..."
                  : "Describe your question or issue..."
            }
            rows={4}
            autoSize={{ minRows: 4, maxRows: 8 }}
          />
        </Form.Item>
        {showAttachments && (
          <Form.Item
            name="attachments"
            label="Screenshot (optional)"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            extra={`Up to ${MAX_SUPPORT_FILES} images · ${MAX_IMAGE_MB} MB each`}
          >
            <Upload
              listType="picture-card"
              accept="image/jpeg,image/png,image/webp,image/gif"
              maxCount={MAX_SUPPORT_FILES}
              beforeUpload={beforeUploadSupportImage}
            >
              <div style={{ marginTop: 4 }}>
                <PlusOutlined />
                <div style={{ marginTop: 4, fontSize: 12 }}>Add image</div>
              </div>
            </Upload>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
});

export default SupportFeedbackModal;
